import { message, Modal } from "antd";
import cuid from "cuid";
import { defaults, find, get, keyBy, uniq } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  MODAL_STATES,
  PROCESS_TYPES,
  TEMPLATES,
  THEMES_PALETTE,
} from "utils/constants";
import {
  buildCombinations,
  singleCombinationAdditionalInfo,
  toPersistedCombinations,
} from "utils/helpers/combinations";
import Done from "./Done";
import Initial from "./Initial";
import Pending from "./Pending";

const MAX_COMBINATIONS_PER_PROJECT = 5;

const DownloadOrSend = ({
  data,
  open,
  onClose,
  onSendQuote,
  onDownload,
  fields,
  insurancePlans,
  selectedModules,
  project,
}) => {
  const { id } = useParams();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [state, setState] = useState(MODAL_STATES.INITIAL);
  const [addLinks, setAddLinks] = useState(true);
  const [selectedCombinationKeys, setSelectedCombinationKeys] = useState([]);
  const [addLink] = useMutation(CREATE_LINK);
  const [getTracer] = useLazyQuery(TRACER);
  const [updateProject] = useMutation(UPDATE_PROJECT);

  const consumedCombinationKeys = useMemo(
    () => get(project, "fields.consumedCombinations", []),
    [project],
  );

  const availableSlots = Math.max(
    0,
    MAX_COMBINATIONS_PER_PROJECT - consumedCombinationKeys.length,
  );
  const reachedMaxCombinations =
    selectedCombinationKeys.length >= availableSlots;

  const combinations = useMemo(
    () =>
      buildCombinations({
        bases: data,
        upsellsByBase: selectedModules,
      }),
    [data, selectedModules],
  );

  const combinationKey = (combination) =>
    `${get(combination, "baseInsurancePlan.id")}|${
      get(combination, "complementaryInsurancePlan.id") || "none"
    }`;

  const selectedCombinationIds = useMemo(
    () =>
      combinations
        .filter((combination) =>
          selectedCombinationKeys.includes(combinationKey(combination)),
        )
        .map((combination) => combination.id),
    [combinations, selectedCombinationKeys],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedCombinationKeys([]);
  }, [open]);

  const [justifications, setJustifications] = useState(
    get(
      fields,
      "justifications",
      combinations.reduce((acc, combination) => {
        acc[combination.id] = "";
        return acc;
      }, {}),
    ),
  );

  const selectedCombinations = useMemo(
    () =>
      combinations.filter((combination) =>
        selectedCombinationIds.includes(combination.id),
      ),
    [combinations, selectedCombinationIds],
  );

  const persistedSelectedCombinations = useMemo(
    () => toPersistedCombinations(selectedCombinations),
    [selectedCombinations],
  );

  const selectedBaseIds = useMemo(
    () =>
      uniq(
        persistedSelectedCombinations.map(
          (combination) => combination.baseInsurancePlanId,
        ),
      ),
    [persistedSelectedCombinations],
  );

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
  });

  const initialValues = {
    contact: {
      ...get(project, "contact", {}),
      telecoms,
      fields: {
        ...get(project, "contact.fields", {}),
      },
    },
    contract: get(project, "contract"),
  };

  const onCheck = (combinationId, checked) => {
    const combination = find(combinations, { id: combinationId });
    if (!combination) return;
    const key = combinationKey(combination);
    if (consumedCombinationKeys.includes(key)) return;
    if (checked) {
      if (selectedCombinationKeys.includes(key)) return;
      if (selectedCombinationKeys.length >= availableSlots) {
        message.warning(
          `Vous pouvez sélectionner au maximum ${MAX_COMBINATIONS_PER_PROJECT} combinaisons par projet.`,
        );
        return;
      }
      return setSelectedCombinationKeys((prev) => [...prev, key]);
    }
    setSelectedCombinationKeys((prev) => prev.filter((item) => item !== key));
    onJustify(undefined, combinationId);
  };

  const onJustify = (justification, combinationId) =>
    setJustifications((prev) => ({
      ...prev,
      [combinationId]: justification,
    }));

  const buildPayload = (extra = {}) => {
    const newlyConsumedKeys = selectedCombinations.map((combination) =>
      combinationKey(combination),
    );
    const persistedKeyOf = (combination) =>
      `${combination.baseInsurancePlanId}|${
        combination.complementaryInsurancePlanId || "none"
      }`;
    const existingPersistedCombinations = get(fields, "combinations", []);
    const existingKeysSet = new Set(
      existingPersistedCombinations.map(persistedKeyOf),
    );
    const additionalPersistedCombinations =
      persistedSelectedCombinations.filter(
        (combination) => !existingKeysSet.has(persistedKeyOf(combination)),
      );
    const cumulativeCombinations = [
      ...existingPersistedCombinations,
      ...additionalPersistedCombinations,
    ];
    const chosenCombination = singleCombinationAdditionalInfo(
      persistedSelectedCombinations,
    );
    return {
      fields: {
        ...fields,
        ...(chosenCombination
          ? { selectedCombinationId: chosenCombination.combinationId }
          : {}),
        insurancePlans: uniq([
          ...get(fields, "insurancePlans", []),
          ...selectedBaseIds,
        ]),
        lastSelected: selectedBaseIds,
        addLinks,
        justifications,
        upsells: selectedModules,
        combinations: cumulativeCombinations,
        currentDevisCombinationKeys: newlyConsumedKeys,
        consumedCombinations: uniq([
          ...consumedCombinationKeys,
          ...newlyConsumedKeys,
        ]),
        ...extra,
      },
    };
  };

  const sendQuote = (submitForm) => {
    const trigger = () => {
      onSendQuote({
        selectedData: selectedBaseIds,
        payload: buildPayload(),
      });
      onClose();
    };
    if (submitForm) submitForm().then(trigger);
    else trigger();
  };

  const download = (submitForm) => {
    const trigger = () => {
      onDownload({
        setState,
        setSelectedData: () => setSelectedCombinationKeys([]),
        selectedData: selectedBaseIds,
        payload: { locked: true, ...buildPayload() },
      });
    };
    if (submitForm) submitForm().then(trigger);
    else trigger();
  };

  const onCopyLink = () => {
    const template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[
        THEMES_PALETTE.DEFAULT.PROSPECT_PROCESS
      ].PRODUCTS;
    getTracer({
      variables: {
        where: {
          flow: {
            process: {
              theme: THEMES_PALETTE.DEFAULT.PROSPECT_PROCESS,
            },
          },
        },
      },
      onCompleted: ({ tracer }) => {
        const statuses = get(tracer, "flow.process.processStatuses", []);
        const selectedStatus = find(statuses, { template })?.id;
        const url = `${
          process.env.REACT_APP_BASE_URL
        }/#/public/process/init/${get(tracer, "source.id")}`;
        addLink({
          variables: {
            data: {
              url,
              project: { id },
              fields: {
                process: { id: get(tracer, "flow.process.id") },
                status: { id: selectedStatus },
              },
            },
          },
          onCompleted: ({ addLink }) => {
            navigator.clipboard.writeText(`${url}?l=${addLink.id}`);
            message.success(
              DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND.COPIED,
            );
          },
        });
      },
    });
  };

  const onSubmit = (values) => {
    const telecomsArray = Object.entries(values.contact.telecoms || {})
      .map(([system, telecom]) => ({
        id: telecom.id || cuid(),
        system,
        value: telecom.value,
      }))
      .filter((telecom) => telecom.value);
    return new Promise((resolve, reject) => {
      updateProject({
        variables: {
          where: { id: project.id },
          data: {
            contact: {
              ...values.contact,
              telecoms: telecomsArray,
            },
          },
        },
        onCompleted: () => {
          resolve();
        },
        onError: (error) => {
          console.error("Error saving contact:", error);
          message.error(
            "Failed to save contact information. Please try again.",
          );
          reject(error);
        },
      });
    });
  };

  const MODAL_STATE = {
    [MODAL_STATES.INITIAL]: (
      <Initial
        combinations={combinations}
        onSendQuote={sendQuote}
        onDownload={download}
        onCheck={onCheck}
        onJustify={onJustify}
        justifications={justifications}
        selectedCombinationIds={selectedCombinationIds}
        consumedCombinationKeys={consumedCombinationKeys}
        reachedMaxCombinations={reachedMaxCombinations}
        insurancePlans={insurancePlans}
        translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND}
        addLinks={addLinks}
        setAddLinks={setAddLinks}
        onCopyLink={onCopyLink}
        initialValues={initialValues}
        onSubmit={onSubmit}
      />
    ),
    [MODAL_STATES.PENDING]: (
      <Pending translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND} />
    ),
    [MODAL_STATES.DONE]: (
      <Done translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND} />
    ),
  };

  return (
    <Modal open={open} onCancel={onClose} width={"60%"} footer={null}>
      {MODAL_STATE[state]}
    </Modal>
  );
};

export default DownloadOrSend;
